<script lang="ts" setup>
import type { FavoriteListCreateInput } from '~/models/Favorites'
import type { IsValid, ValidateFormResults } from '~/composables/useValidation'
import { useI18n } from 'vue-i18n'
import { reactive, watch } from 'vue'
import { colors } from '~/utils/colors'
import useAuth from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useValidation } from '~/composables/useValidation'
import Button from '~/components/ui/Button.vue'

interface FormData {
  name: string
  note: string
}

const { isLoading } = defineProps<{ isLoading: boolean }>()

const emit = defineEmits<{
  close: []
  create: [item: FavoriteListCreateInput]
}>()

const { t } = useI18n()
const { userId } = useAuth()

const { rules, validateForm } = useValidation()
const form = reactive<FormData>({ name: '', note: '' })
const validationResults = reactive<ValidateFormResults<FormData> & IsValid>({
  name: [],
  note: [],
  isValid: false,
})

const create = () => {
  if (!validationResults.isValid)
    return
  emit('create', {
    ownerUserId: userId ?? '',
    name: form.name,
    description: form.note,
    colorId: Math.floor(Math.random() * colors.length) + 1,
  } as FavoriteListCreateInput)
}

watch(form, () => validateForm(
  form,
  {
    name: [
      { rule: rules.string(), message: t('validation.favorite.name.error') },
      { rule: rules.required(), message: t('validation.general.required.error') },
    ],
    note: [
      { rule: rules.string(), message: t('validation.favorite.note.error') },
    ],
  },
  validationResults,
))
</script>

<template>
  <div class="flex flex-col gap-6">
    <label class="space-y-2">
      <p
        class="text-black"
        v-text="t('favorite.createListNameLabel')"
      />
      <input
        v-model="form.name"
        class="border-gray-10 placeholder:text-gray-40 w-full rounded-xl border bg-white px-4 py-2 text-base transition focus-visible:ring"
        type="text"
        :placeholder="$t('media.filenamePlaceholder')"
        @keydown.enter="create"
      >
    </label>
    <div
      v-if="validationResults.name.some(r => r.isError)"
      class="text-coral-40"
    >
      <div
        v-for="error in validationResults.name"
        :key="error.message"
        v-text="error.message"
      />
    </div>
    <label class="space-y-2">
      <p
        class="text-black"
        v-text="t('favorite.editListNoteLabel')"
      />
      <textarea
        v-model="form.note"
        class="border-gray-10 placeholder:text-gray-40 w-full rounded-xl border bg-white px-4 py-2 text-base transition focus-visible:ring"
        :placeholder="$t('media.writeNote')"
        @keydown.enter="create"
      />
    </label>
    <ul
      v-if="validationResults.note.some(r => r.isError)"
      class="text-coral-40"
    >
      <li
        v-for="(error, i) in validationResults.note"
        :key="`error-${i}`"
        v-text="error.message"
      />
    </ul>
    <div class="flex justify-end gap-2">
      <Button
        class="justify-self-end"
        size="lg"
        :disabled="!validationResults.isValid || isLoading"
        :data-pendo="PendoFeature.Favorites.CreateList"
        @click="create"
      >
        {{ t('save') }}
      </Button>
    </div>
  </div>
</template>
